import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, Sparkles, Download, Wand2, Edit3 } from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { toast } from 'sonner';
import { downloadAutoCarousel, generateAutoCarousel } from '../../lib/api';
import CarouselGeneratorDialog from './CarouselGeneratorDialog';

/**
 * AutoCarouselButton - launches the premium carousel generator dialog so the
 * user can pick tone / density / card count / accent color and then download
 * a 4:5 portrait editorial PDF.
 *
 * After the first generation the design is cached on the analysis doc so:
 *  - "Download PDF" is instant (uses the cached design as-is)
 *  - "Edit & download" fetches the cached design and opens the dialog directly
 *    in the edit stage so the user can tweak headlines/body/brand
 *  - "Re-design with options" reopens the configure stage with regenerate=true
 */
export default function AutoCarouselButton({
  analysisId,
  sourceType, // 'post' | 'pov' | 'prompt'
  sourceIdx = 0,
  variant = 'outline',
  size = 'sm',
  cachedSlides = 0,
  postPreview = '',
  className = '',
}) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [forceRegen, setForceRegen] = useState(false);
  const [initialDesign, setInitialDesign] = useState(null);
  const [initialBrandLabel, setInitialBrandLabel] = useState('');
  const [busy, setBusy] = useState(null); // 'download' | 'edit' | null
  const [slides, setSlides] = useState(cachedSlides || 0);
  const navigate = useNavigate();

  const hasCarousel = slides > 0;

  const resetDialogState = () => {
    setForceRegen(false);
    setInitialDesign(null);
    setInitialBrandLabel('');
  };

  // Send the initial "Design carousel" click to the standalone Designer at
  // /carousel-designer with the post's text pre-filled. The dropdown items
  // for already-cached carousels (Edit / Download / Re-design) keep their
  // existing in-place behavior so users don't lose access to cached decks.
  const openGenerate = () => {
    const clean = (postPreview || '').trim();
    const sourceLabelMap = { post: 'Post', pov: 'PoV', prompt: 'Prompt' };
    const label = `From ${sourceLabelMap[sourceType] || 'Post'} #${(sourceIdx ?? 0) + 1}`;
    navigate('/carousel-designer', {
      state: {
        text: clean,
        sourceMeta: {
          label,
          analysisId,
          sourceType,
          sourceIdx,
        },
      },
    });
  };

  const openEdit = async () => {
    if (busy) return;
    setBusy('edit');
    try {
      // Fetch cached design (regenerate=false returns cache when present)
      const out = await generateAutoCarousel(analysisId, sourceType, sourceIdx, { regenerate: false });
      const d = out?.design || {};
      const cards = d.cards || d.slides || [];
      if (!cards.length) {
        toast.error('No cached carousel found — please generate first');
        return;
      }
      setInitialDesign(d);
      setInitialBrandLabel(out?.brand_label || 'Blayzrr');
      setForceRegen(false);
      setDialogOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || 'Could not load carousel');
    } finally {
      setBusy(null);
    }
  };

  const openRedesign = () => {
    resetDialogState();
    setForceRegen(true);
    setDialogOpen(true);
  };

  const doDownload = async () => {
    if (busy) return;
    setBusy('download');
    try {
      await downloadAutoCarousel(analysisId, sourceType, sourceIdx);
      toast.success('Carousel PDF downloaded');
    } catch (e) {
      toast.error(e?.message || 'Download failed');
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      {!hasCarousel ? (
        <Button
          type="button"
          variant={variant}
          size={size}
          onClick={openGenerate}
          disabled={!!busy}
          className={`gap-1.5 ${className}`}
          data-testid={`auto-carousel-generate-${sourceType}-${sourceIdx}`}
        >
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />}
          Design carousel
        </Button>
      ) : (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              type="button"
              variant={variant}
              size={size}
              disabled={!!busy}
              className={`gap-1.5 ${className}`}
              data-testid={`auto-carousel-menu-${sourceType}-${sourceIdx}`}
            >
              {busy ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Sparkles size={13} className="text-primary" />
              )}
              Carousel ({slides})
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel className="text-[10px] font-mono-edge tracking-wider text-muted-foreground">
              AUTO-DESIGNED CAROUSEL
            </DropdownMenuLabel>
            <DropdownMenuItem
              onClick={openEdit}
              data-testid={`auto-carousel-edit-${sourceType}-${sourceIdx}`}
            >
              <Edit3 size={14} className="mr-2 text-primary" />
              Edit & download
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={doDownload}
              data-testid={`auto-carousel-download-${sourceType}-${sourceIdx}`}
            >
              <Download size={14} className="mr-2 text-primary" />
              Download PDF (no edits)
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={openRedesign}
              data-testid={`auto-carousel-redesign-${sourceType}-${sourceIdx}`}
            >
              <Wand2 size={14} className="mr-2 text-primary" />
              Re-design from scratch
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <CarouselGeneratorDialog
        open={dialogOpen}
        onOpenChange={(v) => {
          setDialogOpen(v);
          if (!v) resetDialogState();
        }}
        analysisId={analysisId}
        sourceType={sourceType}
        sourceIdx={sourceIdx}
        postPreview={postPreview}
        forceRegenerate={forceRegen}
        initialDesign={initialDesign}
        initialBrandLabel={initialBrandLabel}
        onGenerated={(n) => setSlides(n || slides || 1)}
      />
    </>
  );
}
